import { Checkbox } from '@progress/kendo-react-inputs';
import { type FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useContextSelector } from 'use-context-selector';
import { ViewShoppingListsContext } from './ViewShoppingListsContext';

const SELECT_ALL_CHECKBOX_ID = 'view-shopping-lists-select-all';
const SELECT_ALL_CHECKBOX_CLASS_NAME =
  '!m-0 inline-flex items-center leading-none [&.k-checkbox-label]:m-0 [&.k-checkbox-label]:inline-flex [&.k-checkbox-label]:items-center [&.k-checkbox-label]:text-sm [&.k-checkbox-label]:leading-none [&_.k-checkbox-wrap]:m-0 [&_.k-checkbox-wrap]:inline-flex [&_.k-checkbox-wrap]:items-center [&_.k-checkbox-wrap]:leading-none [&_.k-checkbox]:m-0';

const ViewShoppingListsSelectAll: FC = () => {
  const { t } = useTranslation();
  const shoppingLists = useContextSelector(ViewShoppingListsContext, (s) => s.shoppingLists);
  const selectedShoppingListIds = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.selectedShoppingListIds
  );
  const toggleAllShoppingListSelection = useContextSelector(
    ViewShoppingListsContext,
    (s) => s.toggleAllShoppingListSelection
  );

  const selectedVisibleCount = useMemo(
    () => shoppingLists.filter((list) => selectedShoppingListIds.includes(list.id)).length,
    [selectedShoppingListIds, shoppingLists]
  );
  const isAllSelected = shoppingLists.length > 0 && selectedVisibleCount === shoppingLists.length;
  const isPartialSelected = selectedVisibleCount > 0 && selectedVisibleCount < shoppingLists.length;

  const handleSelectAllChange = useCallback(() => {
    toggleAllShoppingListSelection();
  }, [toggleAllShoppingListSelection]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      let selectAllCheckbox = document.getElementById(
        SELECT_ALL_CHECKBOX_ID
      ) as HTMLInputElement | null;

      if (!selectAllCheckbox || selectAllCheckbox.tagName !== 'INPUT') {
        const container = document.getElementById(SELECT_ALL_CHECKBOX_ID);
        selectAllCheckbox = container?.querySelector('input[type="checkbox"]') ?? null;
      }

      if (selectAllCheckbox) {
        selectAllCheckbox.indeterminate = isPartialSelected;
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [isAllSelected, isPartialSelected]);

  if (shoppingLists.length === 0) {
    return null;
  }

  return (
    <div className="flex shrink-0 items-center py-3 leading-none">
      <Checkbox
        id={SELECT_ALL_CHECKBOX_ID}
        checked={isAllSelected}
        onChange={handleSelectAllChange}
        label={t('Select All')}
        className={SELECT_ALL_CHECKBOX_CLASS_NAME}
      />
    </div>
  );
};

export default ViewShoppingListsSelectAll;
